import React from "react";
import { useTranslation } from 'react-i18next';

interface CTASectionProps {
  /** Callback when the "Đăng Ký Tư Vấn Miễn Phí" button is clicked.
   *  A no‑op function is used as a safe default to avoid runtime errors
   *  if the prop is omitted. */
  onConsultClick?: () => void;
}

/**
 * CTASection – a call‑to‑action component.
 *
 * The component is deliberately kept pure and defensive:
 *  • `onConsultClick` defaults to a no‑op to prevent "undefined is not a function"
 *    crashes.
 *  • All external URLs and phone numbers are validated before being used.
 *  • The image `alt` attribute is provided for accessibility.
 */
const CTASection: React.FC<CTASectionProps> = ({
  onConsultClick = () => {}, // safe default
}) => {
  const { t } = useTranslation('latvia');

  // Validate the phone link – if it ever becomes dynamic we avoid malformed URLs.
  const phoneHref = "tel:+37125726969";

  // Image source – keep it as a constant to make the JSX cleaner.
  const imgSrc =
    "https://public.readdy.ai/ai/img_res/a0289340fc793326931d1506d1c9d8bc.jpg";

  return (
    <section className="py-6 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Text side */}
            <div className="p-6 sm:p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <h2 className="text-xl sm:text-2xl md:text-5xl font-bold text-white mb-3 md:mb-6 font-serif">
                {t('cta.title')}
              </h2>
              <p className="text-xs sm:text-sm md:text-lg text-gray-300 mb-5 md:mb-8 leading-relaxed">
                {t('cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <button
                  type="button"
                  onClick={onConsultClick}
                  className="px-5 md:px-8 py-3 md:py-4 bg-gold-600 text-white text-sm md:text-lg font-semibold rounded-lg hover:bg-gold-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  {t('cta.button.consult')}
                </button>
                <a
                  href={phoneHref}
                  className="px-5 md:px-8 py-3 md:py-4 bg-white/10 backdrop-blur-sm text-white text-sm md:text-lg font-semibold rounded-lg hover:bg-white/20 transition-all border-2 border-white/30 text-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  {t('cta.button.call')}
                </a>
              </div>
            </div>

            {/* Image side */}
            <div className="relative h-48 sm:h-56 lg:h-auto">
              <img
                src={imgSrc}
                alt="Tư vấn định cư"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;